import React from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { EGG_LABELS } from './constants';

/**
 * Formatea el código del lote sin duplicar la palabra "Lote".
 * Si el código ya viene con prefijo "Lote ", devuelve tal cual.
 */
const formatLoteLabel = (codigo, loteId) => {
  const code = String(codigo || '').trim();
  if (!code) return `Lote ${(loteId || '').slice(0, 6)}`;
  if (/^lote\b/i.test(code)) return code;
  return `Lote ${code}`;
};

/**
 * Obtiene los lotes disponibles para un tipo de huevo dado, tomando en cuenta
 * la clave alternativa "pequeno" ↔ "pequeño".
 */
const getLotesForTipo = (stockPorLote, tipo) => {
  if (!tipo) return [];
  const primary = stockPorLote?.[tipo];
  if (primary && primary.length) return primary;
  const alt = tipo === 'pequeño' ? 'pequeno' : (tipo === 'pequeno' ? 'pequeño' : null);
  return (alt && stockPorLote?.[alt]) || [];
};

/**
 * Modal de creación / edición de factura.
 * Cada línea requiere: tipo de huevo → lote → cantidad → precio.
 * Los rotos también admiten selección de lote (opcional para no bloquear el flujo).
 */
export const FacturaDialog = ({
  open, onOpenChange,
  editing,
  clientes, stock, stockPorLote = {},
  clienteId, setClienteId,
  onOpenNuevoCliente,
  fecha, setFecha,
  items, addItem, removeItem, updateItem,
  rotos, addRoto, removeRoto, updateRoto,
  obs, setObs,
  totalHuevos, totalRotos, totalMonto,
  saving, onSave,
}) => {
  /**
   * Enter avanza al siguiente <input> visible del formulario.
   * En el último input ejecuta "Crear/Actualizar Factura" automáticamente.
   * No interfiere con Selects (Radix maneja su propio Enter).
   */
  const handleFormEnter = (e) => {
    if (e.key !== 'Enter') return;
    const target = e.target;
    if (!target || target.tagName !== 'INPUT') return;
    const t = (target.type || '').toLowerCase();
    if (t === 'checkbox' || t === 'radio' || t === 'submit' || t === 'button') return;
    e.preventDefault();
    const root = e.currentTarget;
    const inputs = Array.from(
      root.querySelectorAll('input:not([disabled]):not([type="hidden"])'),
    ).filter((el) => el.offsetParent !== null);
    const idx = inputs.indexOf(target);
    if (idx === -1) return;
    if (idx < inputs.length - 1) {
      const next = inputs[idx + 1];
      next.focus();
      try { if (typeof next.select === 'function') next.select(); } catch (_) { /* noop */ }
    } else if (!saving) {
      onSave();
    }
  };

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="modal-factura">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
          {editing ? 'Editar Conduce' : 'Nuevo Conduce'}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5 py-2" onKeyDown={handleFormEnter}>
        {/* Client + Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Cliente *</Label>
            <div className="flex gap-2 mt-1.5">
              <Select value={clienteId} onValueChange={setClienteId}>
                <SelectTrigger className="h-11 flex-1" data-testid="select-cliente-factura">
                  <SelectValue placeholder="Seleccione cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id || c._id} value={c.id || c._id}>
                      {c.nombre} {c.telefono ? `(${c.telefono})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 flex-shrink-0"
                onClick={onOpenNuevoCliente}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label>Fecha *</Label>
            <Input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="h-11 mt-1.5"
            />
          </div>
        </div>

        <Separator />

        {/* Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground tracking-wide">ITEMS DE VENTA</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Una línea por tipo/lote. Elija primero el tipo, luego el lote de donde saldrán esos huevos.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={addItem} data-testid="btn-add-item">
              <Plus className="w-3 h-3 mr-1" /> Agregar línea
            </Button>
          </div>
          <div className="space-y-3">
            {items.map((item, idx) => {
              const lotes = getLotesForTipo(stockPorLote, item.tipo_huevo);
              const loteSel = lotes.find((l) => l.lote_id === item.lote_id);
              const cantidad = parseInt(item.cantidad, 10) || 0;
              const disponible = Number(loteSel?.disponible || 0);
              const excedido = !!item.lote_id && cantidad > disponible;
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    excedido ? 'bg-red-50 border-red-200 dark:bg-red-950/20' : 'bg-accent/30 border-transparent'
                  }`}
                  data-testid={`factura-item-row-${idx}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-3">
                      <Label className="text-xs">Tipo de huevo</Label>
                      <Select value={item.tipo_huevo} onValueChange={(v) => updateItem(idx, 'tipo_huevo', v)}>
                        <SelectTrigger className="h-10 mt-1" data-testid={`select-tipo-${idx}`}>
                          <SelectValue placeholder="Seleccione" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(EGG_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Lote *</Label>
                      <Select
                        value={item.lote_id}
                        onValueChange={(v) => updateItem(idx, 'lote_id', v)}
                        disabled={!item.tipo_huevo}
                      >
                        <SelectTrigger className="h-10 mt-1" data-testid={`select-lote-${idx}`}>
                          <SelectValue placeholder={item.tipo_huevo ? 'Seleccione lote' : '— elige tipo primero —'} />
                        </SelectTrigger>
                        <SelectContent>
                          {lotes.length === 0 && item.tipo_huevo && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">Sin lotes activos con stock</div>
                          )}
                          {lotes.map((l) => (
                            <SelectItem
                              key={l.lote_id}
                              value={l.lote_id}
                              disabled={l.disponible <= 0}
                            >
                              {formatLoteLabel(l.lote_codigo, l.lote_id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        placeholder="0"
                        value={item.cantidad}
                        onChange={(e) => updateItem(idx, 'cantidad', e.target.value)}
                        className={`h-10 mt-1 tabular-nums ${excedido ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                        data-testid={`input-cantidad-${idx}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Precio/u ($)</Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={item.precio_unitario}
                        onChange={(e) => updateItem(idx, 'precio_unitario', e.target.value)}
                        className="h-10 mt-1 tabular-nums"
                      />
                    </div>
                    <div className="md:col-span-1 text-right">
                      <Label className="text-xs">Subtotal</Label>
                      <p className="h-10 flex items-center justify-end font-semibold tabular-nums text-sm">
                        ${(cantidad * (parseFloat(item.precio_unitario) || 0))
                          .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                        onClick={() => removeItem(idx)}
                        disabled={items.length <= 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {excedido && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-700 dark:text-red-400">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Excede el stock disponible del lote. Ajuste la cantidad o cambie de lote.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-3 px-3 py-2 bg-primary/5 rounded-lg">
            <span className="text-sm font-medium tabular-nums">
              {Number(totalHuevos).toLocaleString('en-US')} huevos
            </span>
            <span className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
              Total: ${Number(totalMonto).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        <Separator />

        {/* Huevos Rotos */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground tracking-wide">HUEVOS ROTOS DEL DESPACHO</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Los rotos también se descuentan del lote seleccionado.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={addRoto} data-testid="btn-add-roto">
              <Plus className="w-3 h-3 mr-1" /> Agregar rotos
            </Button>
          </div>
          {rotos.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Sin huevos rotos en este despacho</p>
          ) : (
            <div className="space-y-2">
              {rotos.map((roto, idx) => {
                const lotesR = getLotesForTipo(stockPorLote, roto.tipo_huevo);
                return (
                  <div
                    key={idx}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-3 bg-red-50/50 rounded-lg border border-red-100"
                  >
                    <div className="md:col-span-4">
                      <Label className="text-xs">Tipo de huevo</Label>
                      <Select value={roto.tipo_huevo} onValueChange={(v) => updateRoto(idx, 'tipo_huevo', v)}>
                        <SelectTrigger className="h-10 mt-1">
                          <SelectValue placeholder="Seleccione" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(EGG_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-4">
                      <Label className="text-xs">Lote</Label>
                      <Select
                        value={roto.lote_id}
                        onValueChange={(v) => updateRoto(idx, 'lote_id', v)}
                        disabled={!roto.tipo_huevo}
                      >
                        <SelectTrigger className="h-10 mt-1">
                          <SelectValue placeholder={roto.tipo_huevo ? 'Seleccione lote' : '— elige tipo —'} />
                        </SelectTrigger>
                        <SelectContent>
                          {lotesR.length === 0 && roto.tipo_huevo && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">Sin lotes con stock</div>
                          )}
                          {lotesR.map((l) => (
                            <SelectItem key={l.lote_id} value={l.lote_id} disabled={l.disponible <= 0}>
                              {formatLoteLabel(l.lote_codigo, l.lote_id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        placeholder="0"
                        value={roto.cantidad}
                        onChange={(e) => updateRoto(idx, 'cantidad', e.target.value)}
                        className="h-10 mt-1 tabular-nums"
                      />
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                        onClick={() => removeRoto(idx)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {totalRotos > 0 && (
                <p className="text-xs text-destructive font-medium mt-1 tabular-nums">
                  Total rotos: {Number(totalRotos).toLocaleString('en-US')} (se descontarán del inventario)
                </p>
              )}
            </div>
          )}
        </div>

        <Separator />

        <div>
          <Label>Observaciones</Label>
          <Input
            placeholder="Notas adicionales..."
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            className="h-11 mt-1.5"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button onClick={onSave} disabled={saving} data-testid="btn-crear-factura">
          {saving
            ? (editing ? 'Actualizando...' : 'Creando...')
            : (editing ? 'Actualizar Conduce' : 'Crear Conduce')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};
